// Dashboard + DashboardWidget entities (arc 101 slice 09, task #80) — the two
// `dashboards` / `dashboard_widgets` rows, parsed on the way OUT of the DB
// (`normalizeDashboard` / `normalizeDashboardWidget`) so a drifted write fails loudly at
// read (the same parse gate `tasks`/`agent_templates` use). The WIDGET/LAYOUT
// GRAMMAR itself lives in `../value-objects/dashboard` (task #79); this file adds
// the persisted-row shapes + the ONE write contract (`UpsertDashboardInput`) that
// both the REST CRUD (`apps/api`) and the `dashboard` MCP verb author through.
//
// SCOPE is DERIVED, never stored as its own column (`dashboardScope`): a dashboard
// carries `ownerUserId` (null = account-shared) + a `builtin` flag, and the three
// router scopes (`org | user | custom`) fall out of that pair — built-ins ship as
// `org`/`user` seeds, everything authored via API/MCP is `custom`.
//
// AUTHORING is span-ordered, not layout-authored: a caller emits an ORDERED list
// of `{ spec, span }` widgets and the service assembles the flat 12-col
// `DashboardLayout` from them (mint an id per widget, one layout ref per widget in
// authoring order). So a write can never dangle a layout ref at a missing widget —
// the two are built together from one list. Adopted from tesote.ai's proven
// `UiDashboardUpsert` directive (`packages/shared/src/dashboards.ts`).

import { z } from 'zod';
import {
  DASHBOARD_GRID_COLUMNS,
  DashboardLayoutSchema,
  type DashboardScope,
  WidgetSpecSchema,
} from '../value-objects/dashboard';
import { AccountIdSchema, DashboardIdSchema, DashboardWidgetIdSchema } from '../value-objects/ids';

// ─── entities (persisted rows) ────────────────────────────────────────────────

/**
 * One `dashboard_widgets` row — a tile's content (`spec`: what to read + how to
 * render it) independent of its POSITION (which lives in the parent dashboard's
 * `layout`). Owned by exactly one dashboard (`dashboardId`); the layout PLACES a
 * subset of a dashboard's widgets, so removing a layout ref never deletes the
 * widget row. Parsed on read so a corrupt `spec` (a drifted/hand-edited jsonb)
 * fails loudly rather than reaching a renderer.
 */
export const DashboardWidgetSchema = z.object({
  /** `dwg_<hex>` — the branded id IS the stored text PK (same scheme as tasks/templates). */
  id: DashboardWidgetIdSchema,
  accountId: AccountIdSchema,
  /** The dashboard this widget belongs to; cascade-deleted with it. */
  dashboardId: DashboardIdSchema,
  /** The read-only widget grammar (`WidgetSpec`) stored as jsonb; re-validated here. */
  spec: WidgetSpecSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type DashboardWidget = z.infer<typeof DashboardWidgetSchema>;

/**
 * One `dashboards` row. `ownerUserId` null = account-shared (any member sees it);
 * set = personal to that user (cross-org). `builtin` marks a pre-seeded definition
 * (org/user built-in) vs an API/MCP-authored `custom` board. `layout` is the flat
 * 12-col grid referencing this dashboard's widget ids.
 */
export const DashboardSchema = z.object({
  /** `dsh_<hex>` — the branded id IS the stored text PK. */
  id: DashboardIdSchema,
  accountId: AccountIdSchema,
  /** Owning user (personal scope), or null for an account-shared dashboard. `users.id` is a uuid. */
  ownerUserId: z.string().uuid().nullable(),
  /** Human label, unique per (account, owner) — the `dashboard` upsert key. */
  name: z.string().min(1).max(200),
  /** The 12-col grid, referencing this dashboard's widget ids. */
  layout: DashboardLayoutSchema,
  /** Pre-seeded built-in (org/user) vs an API/MCP-authored custom board. */
  builtin: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type Dashboard = z.infer<typeof DashboardSchema>;

/**
 * Derive a dashboard's router scope from its `(ownerUserId, builtin)` pair — the
 * grouping the dashboard nav renders under (task #81). Authored boards are always
 * `custom`; built-ins are `org` (shared) or `user` (personal). Scope is NEVER a
 * stored column — it is a pure function of the two fields, so the two can never
 * drift out of sync.
 */
export function dashboardScope(d: {
  ownerUserId: string | null;
  builtin: boolean;
}): DashboardScope {
  if (!d.builtin) return 'custom';
  return d.ownerUserId === null ? 'org' : 'user';
}

// ─── write contract (shared by REST CRUD + the `dashboard` MCP verb) ───────────

/**
 * One widget in an upsert — the read-only `spec` plus its column `span` in the
 * 12-col grid. The service places widgets in authoring order (each becomes one
 * layout ref), so the caller never hand-writes a layout and can never dangle a
 * ref. `span` defaults to a full row.
 */
export const UpsertDashboardWidgetInputSchema = z
  .object({
    spec: WidgetSpecSchema,
    span: z.number().int().min(1).max(DASHBOARD_GRID_COLUMNS).default(DASHBOARD_GRID_COLUMNS),
  })
  .strict();
export type UpsertDashboardWidgetInput = z.infer<typeof UpsertDashboardWidgetInputSchema>;

/**
 * The ONE authoring contract both the REST `POST /v1/dashboards` route and the
 * `dashboard` MCP `upsert` action validate through — "same Zod validation" for a
 * phone-authored board and an API call. Keyed by `(accountId, ownerUserId, name)`.
 *
 * A name collision is REFUSED by default (#1099): replacing drops the existing
 * board's widgets and rebuilds its layout behind the same id, which a *create*
 * affordance must never do by accident. A caller that means it opts in —
 * `replace: true` on the REST body / the MCP action — and only then does the
 * upsert live up to its name and become idempotent. `ownerUserId` null = account-shared; a REST
 * caller may only ever set it to their OWN user id (the route enforces that — a
 * board owned by someone else is unrepresentable at the edge). `builtin` is NOT
 * settable here — only the seed path mints built-ins.
 *
 * `min(1)` widgets: a dashboard with no widgets is not useful and `DashboardLayout`
 * itself requires ≥1 row, so an empty board would be a caller error. Every widget
 * `spec` is a read-only `WidgetQuery` by construction (task #79) — a write-shaped
 * query fails to parse HERE, long before the resolver.
 */
export const UpsertDashboardInputSchema = z
  .object({
    name: z.string().min(1).max(200),
    /** Owning user for a personal board, or null (default) for account-shared. */
    ownerUserId: z.string().uuid().nullable().default(null),
    widgets: z.array(UpsertDashboardWidgetInputSchema).min(1).max(50),
  })
  .strict();
export type UpsertDashboardInput = z.infer<typeof UpsertDashboardInputSchema>;
